import {
  Field,
  Image,
  Text,
  RichText,
  getFieldValue,
  withDatasourceCheck,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames/bind';
import React, { ChangeEventHandler, useState } from 'react';
import { getCounties } from '../PlanSearch/API';

import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';
import type { RecordOfFields } from 'models/fields';

import styles from './ZIPCodeSearch.module.scss';
import TextInput from 'components/helpers/TextInput/TextInput';
import Button, { ButtonVariant } from 'components/helpers/Button/Button';
import Select, { type SelectOption } from 'components/helpers/Select/Select';
import Icon from 'components/helpers/Icon/Icon';
import { checkPlans, buildURL } from 'components/PlanData/utilities';
import { County } from 'models/plans-search-types';
import { getCleanID } from 'lib/utils';
import { useI18n } from 'next-localization';
import { ZIPCodeSearchProps, PlanTypeFields } from './ZIPCodeSearch.Types';

const cx = classNames.bind(styles);

const ZIPCodeSearch = (props: ZIPCodeSearchProps): JSX.Element => {
  const {
    planType,
    picture,
    description,
    heading,
    headingStyle,
    headingColor,
    headingLevel,
    planYears,
    zipCodeLabel,
    countyLabel,
    buttonLabel,
  } = props.fields;
  const { t } = useI18n();
  const zipcodeErrorMessage = { value: t('PlansInvalidZipcode') };
  const zipcodeNotCovered = { value: t('PlansZipcodeNotCovered') };
  const countyErrorMessage = { value: t('PlansCountyNotCovered') };
  const { pageEditing } = useSitecoreContext().sitecoreContext;
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [zipcode, setZipcode] = useState<string>('');
  const [counties, setCounties] = useState<County[]>([]);
  const [countyOptions, setCountyOptions] = useState<SelectOption[]>([]);
  const [showError, setShowError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<Field<string>>(zipcodeErrorMessage);
  const [selectedCounty, setSelectedCounty] = useState<number>(-1);

  const titleHeadingLevelItemField = headingLevel?.fields as RecordOfFields;
  const titleHeadingLevelValue = getFieldValue<string>(titleHeadingLevelItemField, 'Value') || 'h1';

  const titleTextStyleItemField = headingStyle?.fields as RecordOfFields;
  const titleTextStyleValue = getFieldValue<string>(titleTextStyleItemField, 'Value') || 'font-style-xl';

  const titleTextColorItemField = headingColor?.fields as RecordOfFields;
  const titleTextColorValue = getFieldValue<string>(titleTextColorItemField, 'Value') || 'purple-60';
  const typeFields = planType?.fields as PlanTypeFields | null;
  const headingTokenRelaced = { value: heading?.value?.replaceAll('{type}', typeFields?.name?.value || '') };
  const buttonTokenReplaced = { value: buttonLabel.value.replaceAll('{type}', typeFields?.name?.value || '') };

  const zipCodeLength = 5;

  planYears.split(',').forEach((x) => {
    const temp = parseInt(x);
    if (!isNaN(temp) && temp > year) setYear(temp);
  });

  const checkZip: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const zip = +e.target.value ? e.target.value : '';
    setZipcode(zip);

    // Zipcode is a 5 digit number
    if (zip.length === zipCodeLength) {
      setShowError(false);
      const tempCounties = await getCounties(zip, planType?.id ?? '');

      if (tempCounties.length == 0) {
        setCounties([]);
        setErrorMessage(zipcodeNotCovered);
        setShowError(true);
      } else if (tempCounties.length == 1) {
        setSelectedCounty(0);
        setCounties(tempCounties);
        setErrorMessage(zipcodeNotCovered);
        setShowError(checkPlans(tempCounties[0], year.toString()));
      } else {
        setSelectedCounty(-1);
        setCounties(tempCounties);
        const options: SelectOption[] = [];

        for (let x = 0; x < tempCounties.length; x++) {
          const option: SelectOption = {
            label: { value: tempCounties[x].CountyName },
            value: x.toString(),
          };
          options.push(option);
        }
        setCountyOptions(options);
      }
    } else {
      setShowError(zip.length > zipCodeLength);
      setErrorMessage(zipcodeErrorMessage);
      setCountyOptions([]);
      setSelectedCounty(-1);
    }
  };

  const changeCounty: ChangeEventHandler<HTMLInputElement> = async (e) => {
    const num = Number.parseInt(e.target.value) ?? -1;
    setSelectedCounty(num);
    setShowError(num < 0);
    setShowError(checkPlans(counties[num], year.toString()));
    setErrorMessage(countyErrorMessage);
  };

  const SearchAndContinue: React.MouseEventHandler = () => {
    if (counties.length == 0 || selectedCounty < 0) {
      setShowError(true);
      return;
    }

    Object.entries(counties[selectedCounty].PlansByYear).forEach(([key, value]) => {
      if (key == year.toString()) {
        const tempQueries: [string, string][] = [
          ['state', value.State],
          ['county', value.County],
          ['year', value.Year],
          ['zipcode', zipcode],
          ['ExistingMember', 'false'],
        ];
        let url = '';
        if (value.NumPlans == 1) {
          url = typeFields?.planDetailPage.url ?? '';
          tempQueries.push(['planId', getCleanID(value.PlanID)]);
        } else {
          url = typeFields?.planOptionsListingPage.url ?? '';
        }
        window.location.href = buildURL(url, null, tempQueries);
        return;
      }
    });
  };

  const bannerClass = picture.value?.src
    ? cx('zip-code-search-banner', 'threecol')
    : cx('zip-code-search-banner', 'twocol');
  const displayClass = cx('display');

  return (
    <div className={cx('zip-code-banner-container')}>
      <div className={bannerClass}>
        {picture.value?.src && <Image field={picture} />}
        <div className={displayClass}>
          <Text
            tag={titleHeadingLevelValue}
            className={cx(titleTextColorValue, titleTextStyleValue)}
            encode={pageEditing}
            field={pageEditing ? heading : headingTokenRelaced}
          />
          <RichText className={cx('body')} field={description} tag="div" />
        </div>
        <div className={cx('displayInput')}>
          <Text tag="div" className={cx('inputLabel')} field={zipCodeLabel} />

          <TextInput maxLength={zipCodeLength} type="number" id="zipcode" pattern="^[0-9]{5}$" onChange={checkZip} />

          {(countyOptions.length > 1 || pageEditing) && (
            <Select
              id="county"
              options={countyOptions}
              onChange={changeCounty}
              className={cx('inputLabel')}
              label={countyLabel.value}
            />
          )}
          {showError && (
            <div className={cx('error')}>
              <Icon variant="error"></Icon>
              <Text className={cx('error')} role="alert" htmlFor="zipcode" field={errorMessage} encode={pageEditing} />
            </div>
          )}
          <Button
            type="submit"
            variant={ButtonVariant.Primary}
            className={cx('submitButton')}
            onClick={SearchAndContinue}
            disabled={pageEditing}
          >
            <Text field={buttonTokenReplaced} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default compose<ZIPCodeSearchProps>(withDatasourceCheck(), withStyles())(ZIPCodeSearch);
